import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { UseQueryOptions } from '@tanstack/react-query';
import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currentTenantId } from '../../../library/brandingCache';
import { addDays, formatDayLabel, startOfWeek, toDateInputValue, toDateTimeInputValue } from '../../../library/dateTimeHelper';
import { EmptySlotResponse } from '../../../library/models/appointment';
import { openHoursOn, WEEKDAY_LABELS, weekdayOf } from '../../../library/models/clinicProgram';
import { ITherapistProgram } from '../../../library/models/therapistProgram';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import {
    clockOf,
    earliestBookable,
    groupByPeriod,
    PATIENT_BOOKING_NOTICE_MINUTES,
    PERIOD_LABELS,
    SlotPeriod,
    slotsByDay,
    weekDays,
} from './slotGrouping';
import {
    dayCellSelectedStyles,
    dayCellStyles,
    dayCountStyles,
    dayNumberStyles,
    dayWeekdayStyles,
    emptyWeekStyles,
    noticeHintStyles,
    loadingBoxStyles,
    periodHeadingStyles,
    slotChipSelectedStyles,
    slotChipStyles,
    slotGridStyles,
    todayMarkStyles,
    weekBarStyles,
    weekRangeStyles,
    weekStripStyles,
} from './slotPicker.styles';

/**
 * Where the picker's two reads come from.
 *
 * The signed-in patient's are the default and need no mention; a caller with no session -- the
 * clinic website's own booking flow -- passes the public equivalents, which answer the same two
 * questions for the clinic the hostname names.
 */
export interface SlotSource {
    slots: (
        from: string, to: string, windowLength: number, therapistId: number
    ) => UseQueryOptions<EmptySlotResponse[], Error>;
    program: (therapistId: number) => UseQueryOptions<ITherapistProgram, Error>;
}

interface SlotPickerProps {
    therapistId: number;
    /** The chosen service's own length, which is what a free window is measured in. */
    durationMinutes: number;
    selected: EmptySlotResponse | null;
    onSelect: (slot: EmptySlotResponse | null) => void;
    /**
     * An appointment to read past, when this picker is being used to move that appointment.
     *
     * Without it the booking occupies its own neighbourhood, so shifting a session by a
     * quarter of an hour is refused the window only itself is standing in -- and the patient
     * is shown a strip that has quietly lost the hour they already have.
     */
    excludeAppointmentId?: number;
    /**
     * The catalogue line being booked, so an hour whose rooms are all taken is not offered.
     * Not needed when moving an appointment, whose room the API reads off it.
     */
    serviceId?: number;
    /** Omitted for everybody signed in; see {@link SlotSource}. */
    source?: SlotSource;
}

/**
 * A week of the therapist's real availability, and the hour chosen out of it.
 *
 * This replaces a native date field and a dropdown of times. Neither could answer the
 * question a patient actually has -- *when is the soonest you can see me* -- because the
 * date field had no idea which days were even open and the dropdown only ever described
 * one of them.
 *
 * **One request covers the whole strip.** `getEmptySlots` has always taken a date range;
 * the old form asked it for `day, day`. Asking for the Monday to the Sunday costs the same
 * round trip and is what makes a free-slot count per day possible at all.
 *
 * **A shut day and a full day are different things and must look different.** The API
 * returns nothing for both, so the clinic's program is what tells them apart:
 * `openHoursOn` is null only when the clinic is closed. A patient told "closed" stops
 * looking; a patient told "fully booked" tries the next week.
 *
 * Counts are of **slots, not hours**. A window is the chosen service's own length, so six
 * free windows of a half-hour service is three hours, and calling that "6 hours free"
 * would be a lie the confirm step would then contradict.
 */
export const SlotPicker = (props: SlotPickerProps): JSX.Element => {
    const { t: translate, i18n } = useTranslation();

    const today: string = toDateInputValue(new Date());
    const [weekStart, setWeekStart] = useState<string>(() => startOfWeek(today));
    const [day, setDay] = useState<string>(today);

    const days: string[] = weekDays(weekStart);

    const source: SlotSource = props.source ?? {
        slots: (from: string, to: string, windowLength: number, therapistId: number) =>
            services.appointments.getEmptySlots(from, to, windowLength, therapistId,
                { excludeAppointmentId: props.excludeAppointmentId, serviceId: props.serviceId }),
        program: (therapistId: number) =>
            services.therapistProgram.getProgram(therapistId, currentTenantId()),
    };

    const { data: slots, isLoading } = useReactQuery<EmptySlotResponse[]>(
        source.slots(weekStart, days[6], props.durationMinutes, props.therapistId)
    );

    // This therapist's own availability -- their program where they have set one, their
    // clinic's where they have not. The same key their diary uses, so it is usually a cache
    // hit; keyed by clinic too, because a therapist id is a user id and one person can
    // practise at two.
    const { data: program } = useReactQuery<ITherapistProgram>(source.program(props.therapistId));

    /*
     * The first hour a patient may take -- four hours from now, which the API enforces as
     * well. Read once per render rather than per slot, so every comparison in this pass is
     * against the same moment -- and so a strip drawn at 08:59:59 cannot disagree with
     * itself about whether one o'clock is still bookable.
     */
    const earliestValue: string = earliestBookable(toDateTimeInputValue(new Date()));
    const byDay: Record<string, EmptySlotResponse[]> = slotsByDay(slots ?? [], earliestValue);

    /**
     * Whether this therapist works that day at all, which is the only way to tell "not here"
     * from "fully booked" -- the slot endpoint answers both with nothing.
     *
     * The therapist's own answer rather than the clinic's, now that the two can differ: a
     * clinic open six days a week with a therapist who works three would otherwise have told
     * a patient the other three were merely full, and sent them back to try again.
     */
    const isUnavailable = (isoDate: string): boolean =>
        !!program && openHoursOn(program, isoDate) === null;

    const goToWeek = (offset: number): void => {
        const nextStart: string = addDays(weekStart, offset);

        setWeekStart(nextStart);
        props.onSelect(null);

        // Land on the first day of the week being moved to rather than keeping a day that
        // is no longer on screen -- except when that is this week, which opens on today.
        setDay(nextStart <= startOfWeek(today) ? today : nextStart);
    };

    const onPickDay = (isoDate: string): void => {
        setDay(isoDate);
        props.onSelect(null);
    };

    const forDay: EmptySlotResponse[] = byDay[day] ?? [];

    return (
        <Box>
            <Box sx={weekBarStyles}>
                <IconButton
                    onClick={() => goToWeek(-7)}
                    // Nothing is bookable in a week that has already happened.
                    disabled={weekStart <= startOfWeek(today)}
                    aria-label={translate(ResourcesIds.BOOK_APPOINTMENT_WEEK_PREVIOUS)}
                >
                    <ChevronLeft />
                </IconButton>

                <Typography sx={weekRangeStyles}>
                    {translate(ResourcesIds.BOOK_APPOINTMENT_WEEK_RANGE, {
                        from: formatDayLabel(days[0], i18n),
                        to: formatDayLabel(days[6], i18n),
                    })}
                </Typography>

                <IconButton
                    onClick={() => goToWeek(7)}
                    aria-label={translate(ResourcesIds.BOOK_APPOINTMENT_WEEK_NEXT)}
                >
                    <ChevronRight />
                </IconButton>
            </Box>

            <Box sx={weekStripStyles}>
                {days.map((isoDate: string) => {
                    const unavailable: boolean = isUnavailable(isoDate);
                    const free: number = (byDay[isoDate] ?? []).length;
                    const isSelected: boolean = isoDate === day;

                    /*
                     * A day earlier this week has no slots left for the same reason a full
                     * one has none, and saying "fully booked" about yesterday is a small
                     * lie that makes the strip look broken. Named for what it is instead.
                     */
                    const isPast: boolean = isoDate < today;

                    /*
                     * The cell's own words, used both as the caption and as the button's
                     * accessible name -- so a screen reader hears "Mon, 17 Aug, 3 free"
                     * rather than the three loose fragments the columns are drawn from.
                     */
                    const countLabel: string = isPast
                        ? translate(ResourcesIds.BOOK_APPOINTMENT_DAY_PAST)
                        : unavailable
                            ? translate(ResourcesIds.BOOK_APPOINTMENT_DAY_UNAVAILABLE)
                            : free === 0
                                ? translate(ResourcesIds.BOOK_APPOINTMENT_DAY_FULL)
                                : translate(ResourcesIds.BOOK_APPOINTMENT_DAY_FREE_COUNT,
                                    { count: free });

                    return (
                        <Button
                            key={isoDate}
                            onClick={() => onPickDay(isoDate)}
                            disabled={isPast || unavailable || free === 0}
                            aria-label={`${formatDayLabel(isoDate, i18n)}, ${countLabel}`}
                            sx={isSelected ? dayCellSelectedStyles : dayCellStyles}
                        >
                            <Typography component="span" sx={dayWeekdayStyles}>
                                {translate(WEEKDAY_LABELS[weekdayOf(isoDate)]).slice(0, 3)}
                            </Typography>
                            <Typography component="span" sx={dayNumberStyles}>
                                {Number(isoDate.slice(8, 10))}
                            </Typography>
                            <Typography component="span" sx={dayCountStyles}>
                                {countLabel}
                            </Typography>
                            {isoDate === today && <Box sx={todayMarkStyles} />}
                        </Button>
                    );
                })}
            </Box>

            {/*
              * Said up front rather than only when today comes out empty: a patient at 11:00
              * looking at a strip that starts at 15:00 would otherwise read the missing hours
              * as booked, and not know that a phone call gets them one.
              */}
            <Typography sx={noticeHintStyles}>
                {translate(ResourcesIds.BOOK_APPOINTMENT_NOTICE_HINT, {
                    hours: PATIENT_BOOKING_NOTICE_MINUTES / 60,
                })}
            </Typography>

            {isLoading ? (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress size={24} />
                </Box>
            ) : forDay.length === 0 ? (
                <Typography sx={emptyWeekStyles}>
                    {translate(
                        Object.keys(byDay).length === 0
                            ? ResourcesIds.BOOK_APPOINTMENT_NO_SLOTS_THIS_WEEK
                            : ResourcesIds.BOOK_APPOINTMENT_NO_SLOTS
                    )}
                </Typography>
            ) : (
                groupByPeriod(forDay).map(([period, inPeriod]: [SlotPeriod, EmptySlotResponse[]]) => (
                    <Box key={period}>
                        <Typography sx={periodHeadingStyles}>
                            {translate(PERIOD_LABELS[period])}
                        </Typography>

                        <Box sx={slotGridStyles}>
                            {inPeriod.map((slot: EmptySlotResponse) => {
                                const isSelected: boolean =
                                    props.selected?.startTime === slot.startTime;

                                return (
                                    <Button
                                        key={slot.startTime}
                                        variant="outlined"
                                        onClick={() => props.onSelect(slot)}
                                        sx={isSelected ? slotChipSelectedStyles : slotChipStyles}
                                    >
                                        {clockOf(slot.startTime)}
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                ))
            )}
        </Box>
    );
};
